import clsx from 'clsx';
import type { ReactNode } from 'react';
import {
  FieldError as RACFieldError,
  Label as RACLabel,
  Text as RACText,
  type ValidationResult as RACValidationResult,
} from 'react-aria-components';
import { IconWarning } from '../../../icons/index.js';
import type { FieldError } from '../../types.js';
import { Text } from '../../typography/text.js';
import { textStyle } from '../../typography/utils.js';

/**
 * Renders a label for a form field.
 * @internal
 */
export function FormFieldLabel({
  children,
  isRequired,
  isDisabled,
}: {
  children: ReactNode | undefined;
  isRequired: boolean | undefined;
  isDisabled?: boolean | undefined;
}) {
  if (!children) {
    return null;
  }

  return (
    <div
      className={clsx('cim-form-field-label', textStyle({ variant: 'medium', tone: isDisabled ? 'muted' : 'base' }))}
    >
      <RACLabel>{children}</RACLabel>
      {isRequired && (
        <Text tone="critical" as="span" aria-hidden>
          *
        </Text>
      )}
    </div>
  );
}

/**
 * Renders a description for a form field.
 * @internal
 */
export function FormFieldDescription({ children }: { children: string | undefined }) {
  if (!children) {
    return null;
  }

  return (
    <RACText
      slot="description"
      className={clsx('cim-form-field-description', textStyle({ variant: 'small', tone: 'subtle' }))}
    >
      {children}
    </RACText>
  );
}

/**
 * Renders an error message for a form field.
 *
 * Must be rendered unconditionally, as it can be populated with browser validation errors
 * even if an explicit error message isn't provided.
 *
 * @internal
 */
export function FormFieldError({ children }: { children: FieldError }) {
  return (
    <RACFieldError className={clsx('cim-form-field-error', textStyle({ variant: 'small', tone: 'critical' }))}>
      {(validation) => renderFieldError(children, validation)}
    </RACFieldError>
  );
}

function renderFieldError(fieldError: FieldError, validation: RACValidationResult): string | ReactNode | undefined {
  const errors =
    typeof fieldError === 'function' ? fieldError(validation) : (fieldError ?? validation.validationErrors);

  if (!errors || !errors.length) {
    return null;
  }

  return (
    <ul className="cim-form-field-error-list">
      {typeof errors === 'object' ? (
        errors.map((error) => <ErrorMessage key={error} message={error} />)
      ) : (
        <ErrorMessage message={errors} />
      )}
    </ul>
  );
}

function ErrorMessage({ message }: { message: string }) {
  return (
    <li className="cim-form-field-error-message">
      <div className="cim-form-field-error-icon-wrapper" aria-hidden>
        <IconWarning />
      </div>
      {message}
    </li>
  );
}

/**
 * Renders an error message for `errorMessage` slot.
 * Used by `TagGroup` to show error messages, since `FieldError` is not supported as it is not a form field.
 *
 * @internal
 */
export function FormFieldErrorText({ children }: { children: string }) {
  return (
    <RACText
      slot="errorMessage"
      className={clsx('cim-form-field-error', textStyle({ variant: 'small', tone: 'critical' }))}
    >
      <ErrorMessage message={children} />
    </RACText>
  );
}
